import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import AdminLayout from '../../components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import StatusBadge from '../../components/shared/StatusBadge';
import { getApiError } from '../../lib/utils';
import { ArrowLeft, TrendingUp, Calendar, ChevronRight, ChevronDown, Building2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount || 0);

const monthName = (m) => {
  const months = ['','January','February','March','April','May','June','July','August','September','October','November','December'];
  return months[m] || '';
};

const CustomerBilling = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [currentBill, setCurrentBill] = useState(null);
  const [monthlyBills, setMonthlyBills] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedBillItems, setSelectedBillItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [customerId]);

  const fetchData = async () => {
    try {
      const [custRes, currentRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/api/customers`),
        axios.get(`${API_URL}/api/admin/billing/${customerId}/current`),
        axios.get(`${API_URL}/api/admin/billing/${customerId}/history`)
      ]);
      const cust = (custRes.data.customers || []).find(c => c.customer_id === customerId);
      setCustomer(cust || { company_name: 'Unknown', customer_id: customerId });
      setCurrentBill(currentRes.data);
      setMonthlyBills(historyRes.data.months || []);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to load billing'));
    } finally {
      setLoading(false);
    }
  };

  const viewMonthDetail = async (year, month) => {
    const key = `${year}-${month}`;
    if (selectedMonth === key) {
      setSelectedMonth(null);
      setSelectedBillItems([]);
      return;
    }
    setDetailLoading(true);
    setSelectedMonth(key);
    try {
      const res = await axios.get(`${API_URL}/api/admin/billing/${customerId}/month/${year}/${month}`);
      setSelectedBillItems(res.data.waybills || []);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to load month details'));
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      </AdminLayout>
    );
  }

  const WaybillTable = ({ waybills }) => (
    <div className="overflow-x-auto">
      <table className="data-table">
        <thead>
          <tr>
            <th>Reference</th>
            <th>Status</th>
            <th>Route</th>
            <th>Date</th>
            <th className="text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {waybills.map((wb) => (
            <tr
              key={wb.waybill_id}
              className="cursor-pointer hover:bg-slate-50"
              onClick={() => navigate(`/admin/shipment/${wb.waybill_id}`, { state: { from: `/admin/customer/${customerId}/billing` } })}
            >
              <td className="font-medium text-teal-700">{wb.reference_number}</td>
              <td><StatusBadge status={wb.status} /></td>
              <td>{wb.origin_city} → {wb.destination_city}</td>
              <td className="text-slate-500">{wb.date}</td>
              <td className="text-right font-semibold">{formatCurrency(wb.quoted_amount)}</td>
            </tr>
          ))}
          {waybills.length === 0 && (
            <tr><td colSpan="5" className="text-center text-slate-400 py-4">No waybills</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <AdminLayout>
      <div className="animate-fade-in space-y-6" data-testid="admin-customer-billing">
        <div>
          <Link to="/admin/customers" className="inline-flex items-center text-sm text-slate-500 hover:text-teal-700 mb-2">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Customers
          </Link>
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-lg bg-teal-50 flex items-center justify-center">
              <Building2 className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h1 className="page-title">{customer?.company_name}</h1>
              <p className="text-sm text-slate-500">Billing History</p>
            </div>
          </div>
        </div>

        {/* Current Month */}
        <Card className="card-elevated border-l-4 border-l-teal-500" data-testid="admin-current-month-bill">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="h-10 w-10 rounded-lg bg-teal-50 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-teal-600" />
                </div>
                <div>
                  <CardTitle className="card-title">Current Month</CardTitle>
                  <p className="text-xs text-slate-400">{currentBill?.month_label}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-slate-900" data-testid="admin-current-total">
                  {formatCurrency(currentBill?.total)}
                </p>
                <p className="text-xs text-slate-400">{currentBill?.waybill_count} waybill{currentBill?.waybill_count !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </CardHeader>
          {currentBill?.waybills?.length > 0 && (
            <CardContent className="p-0">
              <WaybillTable waybills={currentBill.waybills} />
            </CardContent>
          )}
          {(!currentBill?.waybills || currentBill.waybills.length === 0) && (
            <CardContent>
              <p className="text-sm text-slate-400 text-center py-4">No billable waybills this month</p>
            </CardContent>
          )}
        </Card>

        {/* Previous Months */}
        <div>
          <h2 className="text-lg font-semibold text-slate-800 mb-3">Previous Months</h2>
          {monthlyBills.length === 0 ? (
            <Card className="card-elevated">
              <CardContent className="py-8">
                <p className="text-sm text-slate-400 text-center">No previous billing history</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {monthlyBills.map((bill) => {
                const key = `${bill.year}-${bill.month}`;
                const isOpen = selectedMonth === key;
                return (
                  <Card key={key} className={`card-elevated transition-all ${isOpen ? 'ring-2 ring-teal-200' : ''}`}>
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => viewMonthDetail(bill.year, bill.month)}
                      data-testid={`admin-month-bill-${key}`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center">
                          <Calendar className="h-4 w-4 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-800">{monthName(bill.month)} {bill.year}</p>
                          <p className="text-xs text-slate-400">{bill.waybill_count} waybill{bill.waybill_count !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <p className="text-lg font-bold text-slate-900">{formatCurrency(bill.total)}</p>
                        {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                      </div>
                    </div>
                    {isOpen && (
                      <div className="border-t border-slate-100">
                        {detailLoading ? (
                          <div className="flex items-center justify-center py-6">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-teal-600"></div>
                          </div>
                        ) : (
                          <WaybillTable waybills={selectedBillItems} />
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default CustomerBilling;
